import { getRetailerToken } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useMutation as useDeleteBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/delete-hooks";
import {
  useQuery as useFetchBoardQuery,
  getQueryKey as getFetchBoardQueryKey,
} from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/get-hooks";
import { useMutation as useUpdateBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/put-hooks";
import { getQueryKey as getFetchDefaultBoardQueryKey } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/default/get-hooks";
import { useMutation as useCreateBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/post-hooks";
import { QueryParameters } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/scroll/get";
import {
  useQuery as useFetchBoardsQuery,
  getQueryKey as getFetchBoardsQueryKey,
} from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/scroll/get-hooks";
import { ICreateRetailerBoardRequest } from "@faire/web-api--source/indigofair/data/ICreateRetailerBoardRequest";
import { IGetRetailerBoardResponse } from "@faire/web-api--source/indigofair/data/IGetRetailerBoardResponse";
import { IGetRetailerBoardsScrollResponse } from "@faire/web-api--source/indigofair/data/IGetRetailerBoardsScrollResponse";
import { IRetailerBoard } from "@faire/web-api--source/indigofair/data/IRetailerBoard";
import { IUpdateRetailerBoardRequest } from "@faire/web-api--source/indigofair/data/IUpdateRetailerBoardRequest";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import React from "react";

const PARAMS = QueryParameters.build({ all_items: true });

export const useFetchAllBoards = () => {
  const retailerToken = getRetailerToken();
  const query = useFetchBoardsQuery(retailerToken, PARAMS, {
    refetchOnMount: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useFetchSingleBoard = (boardToken: string | undefined) => {
  const retailerToken = getRetailerToken();
  const {
    data,
    isPending: isLoading,
    refetch,
  } = useFetchBoardQuery(retailerToken, boardToken ?? "", {
    refetchOnMount: false,
  });

  return { board: data?.board, isLoading, refetch };
};

export const useCreateBoard = () => {
  const queryClient = useQueryClient();
  const retailerToken = getRetailerToken();
  const mutation = useCreateBoardMutation({
    onSuccess: (data) => {
      updateAllBoardsCache(
        queryClient,
        retailerToken,
        data.board?.token ?? "",
        data.board
      );
    },
  });

  const createBoard = React.useCallback(
    (name: string, isPrivate: boolean) => {
      return mutation.mutateAsync([
        retailerToken,
        ICreateRetailerBoardRequest.build({ name, is_private: isPrivate }),
      ]);
    },
    [mutation, retailerToken]
  );

  return { createBoard };
};

export const useDeleteBoard = () => {
  const queryClient = useQueryClient();
  const mutation = useDeleteBoardMutation();
  const retailerToken = getRetailerToken();

  const deleteBoard = React.useCallback(
    async (boardToken: string) => {
      const response = await mutation.mutateAsync([retailerToken, boardToken]);
      if (!mutation.isError) {
        updateAllBoardsCache(queryClient, retailerToken, boardToken, undefined);
      }
      return response;
    },
    [queryClient, mutation, retailerToken]
  );

  return { deleteBoard };
};

export const useUpdateBoard = () => {
  const queryClient = useQueryClient();
  const mutation = useUpdateBoardMutation();
  const retailerToken = getRetailerToken();

  const updateBoard = React.useCallback(
    async (boardToken: string, updatedBoardData: Partial<IRetailerBoard>) => {
      const response = await mutation.mutateAsync([
        retailerToken,
        boardToken,
        IUpdateRetailerBoardRequest.build(updatedBoardData),
      ]);
      if (!mutation.isError) {
        updateBoardsCaches(
          queryClient,
          retailerToken,
          boardToken,
          updatedBoardData
        );
      }
      return response;
    },
    [queryClient, mutation, retailerToken]
  );

  return { updateBoard };
};

export const updateBoardsCaches = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  updatedBoardData: Partial<IRetailerBoard>
) => {
  updateSingleBoardCache(
    queryClient,
    retailerToken,
    boardToken,
    updatedBoardData
  );
  updateAllBoardsCache(
    queryClient,
    retailerToken,
    boardToken,
    updatedBoardData
  );
  updateDefaultBoardCache(queryClient, retailerToken, updatedBoardData);
};

const updateAllBoardsCache = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  updatedBoardData: Partial<IRetailerBoard> | undefined
) => {
  // this cache is used for a list of all retailer boards data
  const fetchAllBoardsKey = getFetchBoardsQueryKey(retailerToken, PARAMS);
  queryClient.setQueryData<IGetRetailerBoardsScrollResponse>(
    fetchAllBoardsKey,
    (cached) => {
      if (!cached) {
        return;
      }
      if (!updatedBoardData) {
        return {
          ...cached,
          boards: cached.boards.filter((board) => board.token !== boardToken),
        };
      }
      if (cached.boards.find((board) => board.token === boardToken)) {
        return {
          ...cached,
          boards: cached.boards.map((board) =>
            board.token === boardToken
              ? { ...board, ...updatedBoardData }
              : board
          ),
        };
      } else {
        return {
          ...cached,
          boards: [IRetailerBoard.build(updatedBoardData)].concat(
            cached.boards
          ),
        };
      }
    }
  );
};

const updateSingleBoardCache = (
  queryClient: QueryClient,
  retailerToken: string,
  boardToken: string,
  updatedBoardData: Partial<IRetailerBoard>
) => {
  // this cache is used for a single board data
  const fetchBoardKey = getFetchBoardQueryKey(retailerToken, boardToken);
  queryClient.setQueryData<IGetRetailerBoardResponse>(
    fetchBoardKey,
    (cached) => {
      if (!cached) {
        return;
      }
      cached.board = {
        ...IRetailerBoard.build(cached.board),
        ...updatedBoardData,
      };
      return cached;
    }
  );
};

const updateDefaultBoardCache = (
  queryClient: QueryClient,
  retailerToken: string,
  updatedBoardData: Partial<IRetailerBoard>
) => {
  // this cache is used for the default board data
  const fetchDefaultBoardKey = getFetchDefaultBoardQueryKey(retailerToken);
  queryClient.setQueryData<IGetRetailerBoardResponse>(
    fetchDefaultBoardKey,
    (cached) => {
      if (!cached) {
        return;
      }
      cached.board = {
        ...IRetailerBoard.build(cached.board),
        ...updatedBoardData,
      };
      return cached;
    }
  );
};
